import React, {
  useState, useEffect, useRef, useContext,
} from 'react';
import { ProductCarouselSlide, ProductCarousel } from 'anf-core-react';
import useImageCarousel from './CarouselHook';
import MeasurementsAndStyliticsOverlay from './MeasurementsAndStyliticsOverlay';
import ProductPageContext from '../../contexts/ProductPageContext';
import AltImageSkeletonLoading from './AltImageSkeletonLoading';
import ImageMagnifier from '../../components/ImageMagnifier/ImageMagnifier';

export default function AltImageThumbnails({
  productImages,
  activeProduct,
  brand,
  handleImageErrorOrRemoval,
  hasPDPMFEBelowTheFold,
  isMobile,
  tmntData,
  onClick,
  hasModelMeasurement,
  isQuickview,
}) {
  const ProductPageState = useContext(ProductPageContext);
  const [isLoading, setIsLoading] = useState(true);
  const loadedImages = useRef(0);
  const totalImages = productImages.length;
  const showModelMeasurements = isMobile && hasModelMeasurement;
  const {
    setImageData, onKeyDown, visibleTabID, setVisibleTabID,
  } = useImageCarousel(tmntData, handleImageErrorOrRemoval, ['ArrowRight', 'ArrowLeft']);

  const handleImageLoad = () => {
    loadedImages.current += 1;
    if (loadedImages.current === totalImages) {
      setIsLoading(false);
    }
  };

  const handleImageError = (imageId) => {
    setIsLoading(false);
    handleImageErrorOrRemoval(imageId);
  };

  useEffect(() => {
    setIsLoading(true);
    loadedImages.current = 0;
  }, [ProductPageState?.productId]);

  useEffect(() => {
    setVisibleTabID('slide-1');
  }, [productImages, setVisibleTabID]);

  const setImageSlides = (imageData, index) => {
    const currentIndex = index + 1;
    const altText = tmntData.mainImageAltText.value;

    const newImageSlide = {
      id: `slide-${currentIndex}`,
      isVisible: visibleTabID === `slide-${currentIndex}`,
      key: `thumbnail-images-${imageData.mainImageSrc}`,
      labelledBy: `control-slide-${currentIndex}`,
    };

    return (
      <ProductCarouselSlide
        id={newImageSlide.id}
        isVisible={newImageSlide.isVisible}
        key={newImageSlide.key}
        labelledBy={newImageSlide.labelledBy}
        ariaLabelledBy={newImageSlide.labelledBy}
        className="quickview-magnifier-slide"
      >
        <ImageMagnifier
          originalSrc={imageData.mainImageSrc}
          zoomSrc={imageData.zoomImageSrc}
          alt={altText.replace('{0}', imageData.type)}
          onClick={(event) => onClick?.(event, imageData)}
          onError={() => handleImageError(imageData.imageId)}
          onLoad={() => handleImageLoad()}
          ariaLabel={tmntData?.imageMagnifierAriaLabel?.value}
        />

        <MeasurementsAndStyliticsOverlay
          tmntData={tmntData}
          brand={brand}
          activeProduct={activeProduct}
          isMobile={isMobile}
          hasPDPMFEBelowTheFold={hasPDPMFEBelowTheFold}
          isQuickview={isQuickview}
        />
      </ProductCarouselSlide>

    );
  };

  const buildImagesObject = (productImagesArray) => {
    const imageButtons = [];
    const imageSlides = [];

    productImagesArray?.forEach((imageData, index) => {
      imageButtons.push(setImageData(imageData, index));
      imageSlides.push(setImageSlides(imageData, index));
    });

    return ({
      imageButtons,
      imageSlides,
    });
  };

  const imagesData = buildImagesObject(productImages);
  const shouldShowControls = imagesData.imageButtons.length > 1;

  return (
    <div
      className={`pdp-mobile-alt-image-slider-wrapper ${showModelMeasurements ? 'has-model-measurement' : ''}`}
      data-testid="alt-image-thumbnails"
    >
      {(isLoading) && (
        <AltImageSkeletonLoading
          hasFitInfo={hasModelMeasurement}
          totalImages={totalImages}
          isMobile={isMobile}
        />
      )}
      <div className={isLoading ? 'hide-mobile-alt-images' : 'display-mobile-alt-images'}>
        <ProductCarousel
          controls={{
            buttons: shouldShowControls ? imagesData.imageButtons : [],
            labelText: tmntData.thumbnailImagesAriaLabel.value,
            ariaLabel: tmntData.thumbnailImagesAriaLabel.value,
            onKeyDown: shouldShowControls ? onKeyDown(imagesData.imageButtons) : null,
          }}
          ariaLabel={tmntData.productCarouselAriaLabel.value}
          orientation="horizontal"
          classListForTabButtons={!shouldShowControls ? 'hide-thumbnails' : ''}
        >
          {imagesData.imageSlides}
        </ProductCarousel>
      </div>
    </div>
  );
}
